import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminRoute } from "./components/AdminRoute";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyPhonePage from "./pages/VerifyPhonePage";
import BuyTicketsPage from "./pages/BuyTicketsPage";
import MyTicketsPage from "./pages/MyTicketsPage";
import RankingPage from "./pages/RankingPage";
import HistoryPage from "./pages/HistoryPage";
import ProfilePage from "./pages/ProfilePage";
import ClaimPrizePage from "./pages/ClaimPrizePage";
import ReferralsPage from "./pages/ReferralsPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import FreeRafflePage from "./pages/FreeRafflePage";
import StickersPage from "./pages/StickersPage";
import HowToParticipatePage from "./pages/HowToParticipatePage";
import TransparencyPage from "./pages/TransparencyPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminRaffles from "./pages/admin/AdminRaffles";
import AdminClaims from "./pages/admin/AdminClaims";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminReferrals from "./pages/admin/AdminReferrals";
import AdminTickets from "./pages/admin/AdminTickets";
import AdminSocialProof from "./pages/admin/AdminSocialProof";
import AdminFreeRaffle from "./pages/admin/AdminFreeRaffle";
import AdminParticipants from "./pages/admin/AdminParticipants";
import AdminPlatformStats from "./pages/admin/AdminPlatformStats";
import LandingPage from "./pages/LandingPage";

// Components
import { BottomNav } from "./components/BottomNav";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { SocialProofToast } from "./components/SocialProofToast";

import "@/App.css";

function AppLayout({ children, showNav = true, showHeader = true, showFooter = false, showSocialProof = false }) {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {showHeader && <Header />}
      <main className={`flex-1 ${showNav ? 'pb-20' : ''}`}>
        {children}
      </main>
      {showFooter && <Footer />}
      {showNav && <BottomNav />}
      {showSocialProof && <SocialProofToast />}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/landing" element={<LandingPage />} />
          
          {/* New Public Info Pages */}
          <Route path="/como-participar" element={
            <AppLayout showFooter={true}>
              <HowToParticipatePage />
            </AppLayout>
          } />
          <Route path="/transparencia" element={
            <AppLayout showFooter={true}>
              <TransparencyPage />
            </AppLayout>
          } />
          
          {/* Public Home - Accessible without login */}
          <Route path="/" element={
            <AppLayout showFooter={true} showSocialProof={true}>
              <HomePage />
            </AppLayout>
          } />
          
          <Route path="/verify-phone" element={
            <ProtectedRoute>
              <AppLayout showNav={false}>
                <VerifyPhonePage />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/buy/:raffleId" element={
            <ProtectedRoute>
              <AppLayout showSocialProof={true}>
                <BuyTicketsPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/my-tickets" element={
            <ProtectedRoute>
              <AppLayout showSocialProof={true}>
                <MyTicketsPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/ranking" element={
            <ProtectedRoute>
              <AppLayout>
                <RankingPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/history" element={
            <ProtectedRoute>
              <AppLayout>
                <HistoryPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/profile" element={
            <ProtectedRoute>
              <AppLayout showFooter={true} showSocialProof={true}>
                <ProfilePage />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/referrals" element={
            <ProtectedRoute>
              <AppLayout>
                <ReferralsPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/free-raffle" element={
            <AppLayout showSocialProof={true}>
              <FreeRafflePage />
            </AppLayout>
          } />
          
          <Route path="/stickers" element={
            <AppLayout>
              <StickersPage />
            </AppLayout>
          } />
          
          <Route path="/claim/:raffleId" element={
            <ProtectedRoute>
              <AppLayout showNav={false}>
                <ClaimPrizePage />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <AdminRoute>
              <AppLayout showNav={false}>
                <AdminDashboard />
              </AppLayout>
            </AdminRoute>
          } />
          
          <Route path="/admin/raffles" element={
            <AdminRoute>
              <AppLayout showNav={false}>
                <AdminRaffles />
              </AppLayout>
            </AdminRoute>
          } />
          
          <Route path="/admin/claims" element={
            <AdminRoute>
              <AppLayout showNav={false}>
                <AdminClaims />
              </AppLayout>
            </AdminRoute>
          } />
          
          <Route path="/admin/payments" element={
            <AdminRoute>
              <AppLayout showNav={false}>
                <AdminPayments />
              </AppLayout>
            </AdminRoute>
          } />
          
          <Route path="/admin/referrals" element={
            <AdminRoute>
              <AppLayout showNav={false}>
                <AdminReferrals />
              </AppLayout>
            </AdminRoute>
          } />
          
          <Route path="/admin/tickets" element={
            <AdminRoute>
              <AppLayout showNav={false}>
                <AdminTickets />
              </AppLayout>
            </AdminRoute>
          } />
          
          <Route path="/admin/social-proof" element={
            <AdminRoute>
              <AppLayout showNav={false}>
                <AdminSocialProof />
              </AppLayout>
            </AdminRoute>
          } />
          
          <Route path="/admin/free-raffle" element={
            <AdminRoute>
              <AppLayout showNav={false}>
                <AdminFreeRaffle />
              </AppLayout>
            </AdminRoute>
          } />
          
          <Route path="/admin/participants" element={
            <AdminRoute>
              <AppLayout showNav={false}>
                <AdminParticipants />
              </AppLayout>
            </AdminRoute>
          } />
          
          <Route path="/admin/platform-stats" element={
            <AdminRoute>
              <AppLayout showNav={false}>
                <AdminPlatformStats />
              </AppLayout>
            </AdminRoute>
          } />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        <Toaster 
          position="top-center"
          toastOptions={{
            style: {
              background: '#1e293b',
              color: '#fff',
              borderRadius: '12px',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
